import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { Tabs } from "../../../../ui/components/Tabs";
import { IconButton } from "../../../../ui/components/IconButton";
import { Badge } from "../../../../ui/components/Badge";
import { Checkbox } from "../../../../ui/components/Checkbox";
import { Loader } from "../../../../ui/components/Loader";
import {
  FeatherSearch,
  FeatherEye,
  FeatherEyeOff,
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherPaperclip,
  FeatherChevronDown,
  FeatherArrowUp,
  FeatherArrowDown,
  FeatherInbox,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { DropdownMenu } from "../../../../ui/components/DropdownMenu";
import { useEmailInbox } from "../hooks/useEmailInbox";
import { EmailDetailsTray } from "../components/EmailDetailsTray";
import {
  EmailIntakeQueueListEntity,
  EmailCategory,
} from "../../domain/entities/EmailIntakeQueueEntity";
import { format, formatDistanceToNow } from "date-fns";

type TabType = "review" | "completed" | "ignored";

export const InboxSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("review");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEmails, setSelectedEmails] = useState<number[]>([]);
  const [selectedEmail, setSelectedEmail] =
    useState<EmailIntakeQueueListEntity | null>(null);
  const [trayOpen, setTrayOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [filterCategory, setFilterCategory] = useState<EmailCategory | null>(
    null
  ); // null = "All Emails"
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const [updatingIds, setUpdatingIds] = useState<Set<number>>(new Set());

  const {
    emails,
    pagination,
    loading,
    error,
    fetchEmails,
    updateStatus,
    setEmails,
    setPagination,
  } = useEmailInbox();

  // Map tab to status
  const getStatusForTab = (tab: TabType): "review" | "pushed" | "ignore" => {
    switch (tab) {
      case "review":
        return "review";
      case "completed":
        return "pushed";
      case "ignored":
        return "ignore";
    }
  };

  // Fetch emails when tab, search, sort, or filter changes
  useEffect(() => {
    const status = getStatusForTab(activeTab);
    fetchEmails({
      status,
      category: filterCategory || undefined,
      search: searchQuery || undefined,
      limit: itemsPerPage,
      offset: currentPage * itemsPerPage,
      sort_order: sortOrder,
      sort_by: "created_at",
    });
  }, [
    activeTab,
    searchQuery,
    sortOrder,
    filterCategory,
    currentPage,
    fetchEmails,
  ]);

  // Handle tab change
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSelectedEmails([]);
    setCurrentPage(0);
    setFilterCategory(null); // Reset filter when changing tabs
  };

  // Handle bulk ignore
  const handleBulkIgnore = useCallback(async () => {
    if (selectedEmails.length === 0) return;

    // Add IDs to updating set
    setUpdatingIds(new Set(selectedEmails));

    try {
      await updateStatus(selectedEmails, "ignore");
      // Remove items from list instead of refreshing
      setEmails((prevEmails) =>
        prevEmails.filter((email) => !selectedEmails.includes(email.id))
      );
      // Update pagination total
      if (pagination) {
        setPagination({
          ...pagination,
          total: Math.max(0, pagination.total - selectedEmails.length),
        });
      }
      setSelectedEmails([]);
    } catch (error) {
      console.error("Failed to ignore emails:", error);
    } finally {
      setUpdatingIds(new Set());
    }
  }, [selectedEmails, updateStatus, pagination, setEmails, setPagination]);

  // Handle individual email actions
  const handleViewEmail = (email: EmailIntakeQueueListEntity) => {
    setSelectedEmail(email);
    setTrayOpen(true);
  };

  // Handle status update from tray
  const handleTrayStatusUpdate = useCallback(
    async (ids: number[], status: "ignore" | "pushed") => {
      // Add IDs to updating set
      setUpdatingIds(new Set(ids));

      try {
        await updateStatus(ids, status);
        // Remove items from list instead of refreshing
        setEmails((prevEmails) =>
          prevEmails.filter((email) => !ids.includes(email.id))
        );
        // Update pagination total
        if (pagination) {
          setPagination({
            ...pagination,
            total: Math.max(0, pagination.total - ids.length),
          });
        }
        setTrayOpen(false);
        setSelectedEmail(null);
      } catch (error) {
        console.error("Failed to update status:", error);
      } finally {
        setUpdatingIds(new Set());
      }
    },
    [updateStatus, pagination, setEmails, setPagination]
  );

  // Handle attachment download
  const handleDownloadAttachment = (url: string, filename: string) => {
    window.open(url, "_blank");
  };

  // Format date with time and relative time
  const formatDateWithRelative = (dateString: string | null) => {
    if (!dateString) return { date: "-", relative: "" };
    try {
      const date = new Date(dateString);
      const dateStr = format(date, "MMM d, h:mm a");
      const relativeStr = formatDistanceToNow(date, { addSuffix: true });
      return { date: dateStr, relative: relativeStr };
    } catch {
      return { date: dateString, relative: "" };
    }
  };

  // Get category badge
  const getCategoryBadge = (category: string | null) => {
    if (!category) return null;

    const categoryLabels: Record<string, string> = {
      ap: "AP",
      ar: "AR",
      bank: "Bank",
      general: "General inbox",
    };

    const variant = category === "general" ? "warning" : "neutral";
    return (
      <Badge variant={variant as any}>
        {categoryLabels[category] || category}
      </Badge>
    );
  };

  // Parse sender name and email from email address
  const parseSenderInfo = (emailStr: string) => {
    const match = emailStr.match(/^(.+?)\s*<(.+)>$|^(.+)$/);
    if (match) {
      const name = match[1] || match[3]?.split("@")[0] || "Unknown";
      const email = match[2] || match[3] || emailStr;
      return { name, email };
    }
    const email = emailStr;
    const name = email.split("@")[0] || "Unknown";
    return { name, email };
  };

  // Handle checkbox selection
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedEmails(emails.map((email) => email.id));
    } else {
      setSelectedEmails([]);
    }
  };

  const handleSelectEmail = (emailId: number, checked: boolean) => {
    if (checked) {
      setSelectedEmails([...selectedEmails, emailId]);
    } else {
      setSelectedEmails(selectedEmails.filter((id) => id !== emailId));
    }
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination && pagination.has_more) {
      setCurrentPage(currentPage + 1);
    }
  };

  const allSelected =
    emails.length > 0 && selectedEmails.length === emails.length;

  return (
    <div className="flex w-full flex-col items-start gap-6 px-6 pb-6 flex-1 overflow-y-auto">
      <div className="flex w-full flex-col items-start gap-4 mobile:flex-col mobile:flex-nowrap mobile:gap-2">
        <div className="flex w-full flex-col items-start gap-1">
          <span className="text-heading-1 font-heading-1 text-default-font">
            Inbox
          </span>
          <span className="text-body font-body text-subtext-color">
            {activeTab === "review"
              ? "Emails that could not be auto-processed"
              : activeTab === "completed"
              ? "Emails that are completed and pushed"
              : "Emails that are ignored"}
          </span>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2">
          <TextField
            className="h-auto w-64 flex-none mobile:grow mobile:shrink-0 mobile:basis-0"
            variant="filled"
            label=""
            helpText=""
            icon={<FeatherSearch />}
          >
            <TextField.Input
              placeholder="Search sender, subject..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(e.target.value)
              }
            />
          </TextField>
          <SubframeCore.DropdownMenu.Root>
            <SubframeCore.DropdownMenu.Trigger asChild={true}>
              <Button
                variant="neutral-secondary"
                iconRight={<FeatherChevronDown />}
              >
                {filterCategory === null
                  ? "All Emails"
                  : filterCategory === "general"
                  ? "General"
                  : filterCategory.toUpperCase()}
              </Button>
            </SubframeCore.DropdownMenu.Trigger>
            <SubframeCore.DropdownMenu.Portal>
              <SubframeCore.DropdownMenu.Content
                side="bottom"
                align="end"
                sideOffset={4}
                asChild={true}
              >
                <DropdownMenu>
                  <DropdownMenu.DropdownItem
                    icon={<FeatherInbox />}
                    onClick={() => {
                      setFilterCategory(null);
                      setCurrentPage(0);
                    }}
                  >
                    All Emails
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    icon={<FeatherInbox />}
                    onClick={() => {
                      setFilterCategory("general");
                      setCurrentPage(0);
                    }}
                  >
                    General
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    icon={<FeatherInbox />}
                    onClick={() => {
                      setFilterCategory("ap");
                      setCurrentPage(0);
                    }}
                  >
                    AP
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    icon={<FeatherInbox />}
                    onClick={() => {
                      setFilterCategory("ar");
                      setCurrentPage(0);
                    }}
                  >
                    AR
                  </DropdownMenu.DropdownItem>
                </DropdownMenu>
              </SubframeCore.DropdownMenu.Content>
            </SubframeCore.DropdownMenu.Portal>
          </SubframeCore.DropdownMenu.Root>
          <SubframeCore.DropdownMenu.Root>
            <SubframeCore.DropdownMenu.Trigger asChild={true}>
              <Button
                variant="neutral-secondary"
                iconRight={<FeatherChevronDown />}
              >
                {sortOrder === "asc" ? "Oldest first" : "Newest first"}
              </Button>
            </SubframeCore.DropdownMenu.Trigger>
            <SubframeCore.DropdownMenu.Portal>
              <SubframeCore.DropdownMenu.Content
                side="bottom"
                align="end"
                sideOffset={4}
                asChild={true}
              >
                <DropdownMenu>
                  <DropdownMenu.DropdownItem
                    icon={<FeatherArrowUp />}
                    onClick={() => {
                      setSortOrder("asc");
                      setCurrentPage(0);
                    }}
                  >
                    Oldest first
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    icon={<FeatherArrowDown />}
                    onClick={() => {
                      setSortOrder("desc");
                      setCurrentPage(0);
                    }}
                  >
                    Newest first
                  </DropdownMenu.DropdownItem>
                </DropdownMenu>
              </SubframeCore.DropdownMenu.Content>
            </SubframeCore.DropdownMenu.Portal>
          </SubframeCore.DropdownMenu.Root>
        </div>
      </div>
      <div className="flex w-full items-start [&_.border-b]:!border-b-0">
        <Tabs>
          <Tabs.Item
            active={activeTab === "review"}
            onClick={() => handleTabChange("review")}
          >
            Review
          </Tabs.Item>
          <Tabs.Item
            active={activeTab === "completed"}
            onClick={() => handleTabChange("completed")}
          >
            Completed
          </Tabs.Item>
          <Tabs.Item
            active={activeTab === "ignored"}
            onClick={() => handleTabChange("ignored")}
          >
            Ignored
          </Tabs.Item>
        </Tabs>
      </div>
      <div className="flex w-full flex-col items-start rounded-md border border-solid border-neutral-border">
        {selectedEmails.length > 0 && (
          <div className="flex w-full items-center gap-3 bg-neutral-50 px-4 py-2">
            <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
              {selectedEmails.length} email
              {selectedEmails.length !== 1 ? "s" : ""} selected
            </span>
            <Button
              variant="neutral-tertiary"
              size="small"
              icon={updatingIds.size > 0 ? undefined : <FeatherEyeOff />}
              onClick={handleBulkIgnore}
              disabled={updatingIds.size > 0}
            >
              {updatingIds.size > 0 ? (
                <>
                  <Loader size="small" />
                  <span className="ml-2">Processing...</span>
                </>
              ) : (
                "Ignore"
              )}
            </Button>
          </div>
        )}
        <div className="flex w-full flex-col items-start">
          {/* Header Row */}
          <div className="flex w-full items-center gap-3 border-b border-solid border-neutral-border bg-neutral-50 px-4 py-3">
            <div className="flex h-4 w-4 flex-none items-center justify-center">
              <Checkbox
                label=""
                checked={allSelected}
                onCheckedChange={handleSelectAll}
              />
            </div>
            <div className="flex min-w-[240px] items-center flex-1">
              <span className="text-caption-bold font-caption-bold text-subtext-color">
                SENDER
              </span>
            </div>
            <div className="flex min-w-[200px] items-center flex-1">
              <span className="text-caption-bold font-caption-bold text-subtext-color">
                SUBJECT
              </span>
            </div>
            <div className="flex w-48 flex-none items-center">
              <span className="text-caption-bold font-caption-bold text-subtext-color">
                REASON
              </span>
            </div>
            <div className="flex w-24 flex-none items-center">
              <span className="text-caption-bold font-caption-bold text-subtext-color">
                FILES
              </span>
            </div>
            <div className="flex w-40 flex-none items-center">
              <span className="text-caption-bold font-caption-bold text-subtext-color">
                RECEIVED
              </span>
            </div>
            <div className="flex w-24 flex-none items-center justify-end">
              <span className="text-caption-bold font-caption-bold text-subtext-color">
                ACTIONS
              </span>
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex w-full items-center justify-center py-12">
              <Loader size="large" />
            </div>
          ) : error ? (
            <div className="flex w-full items-center justify-center py-12">
              <span className="text-body font-body text-error-600">
                {error}
              </span>
            </div>
          ) : emails.length === 0 ? (
            <div className="flex w-full items-center justify-center py-12">
              <span className="text-body font-body text-subtext-color">
                No emails found
              </span>
            </div>
          ) : (
            emails.map((email) => {
              const senderInfo = parseSenderInfo(email.from_email);
              const dateInfo = formatDateWithRelative(
                email.received_at || email.created_at
              );

              return (
                <div
                  key={email.id}
                  className="flex w-full items-center gap-3 border-b border-solid border-neutral-border px-4 py-3 cursor-pointer group-hover/cfb22aba:bg-neutral-50"
                >
                  <div className="flex h-4 w-4 flex-none items-center justify-center">
                    <Checkbox
                      label=""
                      checked={selectedEmails.includes(email.id)}
                      onCheckedChange={(checked) =>
                        handleSelectEmail(email.id, checked)
                      }
                    />
                  </div>
                  <div className="flex min-w-[240px] flex-col items-start gap-0.5 flex-1">
                    <span className="text-body-bold font-body-bold text-default-font">
                      {senderInfo.name}
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      {senderInfo.email}
                    </span>
                  </div>
                  <div className="flex min-w-[200px] items-center overflow-hidden flex-1">
                    <span className="text-body font-body text-default-font truncate">
                      {email.subject || "-"}
                    </span>
                  </div>
                  <div className="flex w-48 flex-none items-center">
                    {getCategoryBadge(email.category)}
                  </div>
                  <div className="flex w-24 flex-none items-center gap-1.5">
                    {email.attachment_count && email.attachment_count > 0 ? (
                      <>
                        <FeatherPaperclip className="text-body font-body text-subtext-color" />
                        <span className="text-body font-body text-default-font">
                          {email.attachment_count}
                        </span>
                      </>
                    ) : (
                      "-"
                    )}
                  </div>
                  <div className="flex w-40 flex-none flex-col items-start gap-0.5">
                    <span className="text-body font-body text-default-font">
                      {dateInfo.date}
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      {dateInfo.relative}
                    </span>
                  </div>
                  <div className="flex w-24 flex-none items-center justify-end gap-1">
                    {updatingIds.has(email.id) ? (
                      <Loader size="small" />
                    ) : (
                      <IconButton
                        variant="neutral-tertiary"
                        size="small"
                        icon={<FeatherEye />}
                        onClick={() => handleViewEmail(email)}
                      />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination && emails.length > 0 && (
        <div className="flex w-full items-center justify-between border-t border-solid border-neutral-border bg-default-background px-8 py-4">
          <span className="text-body font-body text-subtext-color">
            Showing {currentPage * itemsPerPage + 1} to{" "}
            {Math.min((currentPage + 1) * itemsPerPage, pagination.total)} of{" "}
            {pagination.total} emails
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="neutral-secondary"
              icon={<FeatherChevronLeft />}
              onClick={handlePreviousPage}
              disabled={currentPage === 0}
            >
              Previous
            </Button>
            <Button
              variant="neutral-secondary"
              iconRight={<FeatherChevronRight />}
              onClick={handleNextPage}
              disabled={!pagination.has_more}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Email Details Tray */}
      <EmailDetailsTray
        email={selectedEmail}
        open={trayOpen}
        onOpenChange={setTrayOpen}
        onUpdateStatus={handleTrayStatusUpdate}
        onDownloadAttachment={handleDownloadAttachment}
      />
    </div>
  );
};
